import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { Lock, Sparkles, Inbox, LogOut, Loader2, ListChecks, Trash2, PlayCircle, Mail, Eye, Send } from "lucide-react";

const STORAGE = "uc_admin_token";

const CATEGORIES = [
  "Platform Disputes",
  "Creator Contracts",
  "IP & Trademark",
  "Business Setup",
  "Reputation & Online Disputes",
  "Monetization & Legal Growth",
];

const TEMPLATES = [
  "platform_disputes",
  "contracts",
  "ip_trademark",
  "business_setup",
  "reputation",
  "monetization",
];

const adminApi = (token) =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setVerifying(true);
      try {
        await adminApi(token).post("/admin/verify");
        setAuthed(true);
      } catch (e) {
        setAuthed(false);
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    const t = e.target.token.value.trim();
    if (!t) return;
    try {
      await adminApi(t).post("/admin/verify");
      localStorage.setItem(STORAGE, t);
      setToken(t);
      setAuthed(true);
      toast.success("Authenticated");
    } catch (err) {
      toast.error("Invalid token");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE);
    setToken("");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-md rounded-3xl border border-[#272B36] bg-[#121419] p-8">
          <div className="inline-flex items-center gap-2 text-[#D9B36C] text-xs uppercase tracking-widest font-semibold">
            <Lock size={14} /> Admin Access
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold text-white tracking-tight">Sign in</h1>
          <p className="mt-2 text-slate-400 text-sm">Paste the admin token to access the console.</p>
          <input
            type="password"
            name="token"
            required
            data-testid="admin-token-input"
            placeholder="admin token"
            className="mt-6 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#D9B36C]"
          />
          <button
            type="submit"
            disabled={verifying}
            data-testid="admin-login-submit"
            className="mt-4 w-full bg-[#D9B36C] text-black font-semibold py-3 rounded-full disabled:opacity-50"
          >
            {verifying ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return <AdminConsole token={token} onLogout={logout} />;
}

function AdminConsole({ token, onLogout }) {
  const [tab, setTab] = useState("generator");
  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Admin Console</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-white tracking-tight">Upload Counsel HQ</h1>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white border border-[#272B36] rounded-full px-4 py-2">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-[#272B36] overflow-x-auto">
          <TabBtn active={tab === "generator"} onClick={() => setTab("generator")} testid="tab-generator">
            <Sparkles size={14} /> AI Blog Generator
          </TabBtn>
          <TabBtn active={tab === "queue"} onClick={() => setTab("queue")} testid="tab-queue">
            <ListChecks size={14} /> Topic Queue
          </TabBtn>
          <TabBtn active={tab === "brief"} onClick={() => setTab("brief")} testid="tab-brief">
            <Mail size={14} /> Weekly Brief
          </TabBtn>
          <TabBtn active={tab === "inbox"} onClick={() => setTab("inbox")} testid="tab-inbox">
            <Inbox size={14} /> Requests
          </TabBtn>
          <TabBtn active={tab === "network"} onClick={() => setTab("network")} testid="tab-network">
            <Inbox size={14} /> Attorney applications
          </TabBtn>
        </div>

        <div className="mt-8">
          {tab === "generator" && <Generator token={token} />}
          {tab === "queue" && <Queue token={token} />}
          {tab === "brief" && <WeeklyBrief token={token} />}
          {tab === "inbox" && <Inboxx token={token} />}
          {tab === "network" && <NetworkInbox token={token} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-[#D9B36C] text-white" : "border-transparent text-slate-400 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function Generator({ token }) {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [featured, setFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const generate = async (publish) => {
    if (!topic.trim()) return toast.error("Topic required");
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await adminApi(token).post("/admin/blog/generate", {
        topic, category, template, featured, publish,
      });
      setPreview(data.preview || data.post);
      toast.success(publish ? "Published" : "Preview generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-generator">
      <div className="rounded-2xl border border-[#272B36] bg-[#121419] p-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Topic</label>
          <input
            data-testid="gen-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. How creators should handle TikTok shop seller agreement clauses"
            className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#D9B36C]"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Category</label>
            <select data-testid="gen-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Template</label>
            <select data-testid="gen-template" value={template} onChange={(e) => setTemplate(e.target.value)} className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm">
              {TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-[#D9B36C]" />
          Mark as featured
        </label>
        <div className="flex gap-3">
          <button onClick={() => generate(false)} disabled={loading} data-testid="gen-preview-btn" className="inline-flex items-center gap-2 border border-[#272B36] text-white px-5 py-2.5 rounded-full text-sm hover:bg-white/5 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Preview
          </button>
          <button onClick={() => generate(true)} disabled={loading} data-testid="gen-publish-btn" className="inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            Generate & Publish
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-6 rounded-2xl border border-[#272B36] bg-[#121419] p-6" data-testid="gen-preview">
          <p className="text-xs text-[#D9B36C] font-semibold uppercase tracking-widest">{preview.category}</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">{preview.title}</h2>
          <p className="mt-2 text-slate-400 text-sm">{preview.excerpt}</p>
          <pre className="mt-4 max-h-[400px] overflow-auto whitespace-pre-wrap text-xs text-slate-300 bg-[#0B0C10] border border-[#272B36] rounded-xl p-4">{preview.body}</pre>
        </div>
      )}
    </div>
  );
}

function Queue({ token }) {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [featured, setFeatured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [bulking, setBulking] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi(token).get("/admin/topics");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load queue");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);

  const add = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    try {
      await adminApi(token).post("/admin/topics", { topic, category, template, featured });
      setTopic("");
      setFeatured(false);
      toast.success("Topic queued");
      load();
    } catch (err) {
      toast.error("Failed to add");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi(token).delete(`/admin/topics/${id}`);
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await adminApi(token).post("/admin/topics/run-now");
      toast.success(data.published ? "Published next topic" : "Queue is empty");
      load();
    } catch (err) {
      toast.error("Run failed");
    } finally {
      setRunning(false);
    }
  };

  const bulkGenerate = async () => {
    if (!window.confirm("Generate ~100 blog topics via AI and add them to the queue? This uses ~$0.03 of LLM credits and takes ~30-60 seconds.")) return;
    setBulking(true);
    try {
      const { data } = await adminApi(token).post("/admin/topics/bulk-generate", { per_category: 17 });
      toast.success(`Added ${data.total_added} topics to queue`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk generate failed");
    } finally {
      setBulking(false);
    }
  };

  const queued = items.filter((i) => i.status === "queued");
  const done = items.filter((i) => i.status === "published");
  const failed = items.filter((i) => i.status === "failed");

  return (
    <div data-testid="admin-queue">
      <div className="rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-6">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Fully autonomous</p>
        <p className="mt-2 text-white font-display font-semibold text-lg">Publishes Mondays & Thursdays · Auto-refills the queue when low · Cross-posts to LinkedIn/X via webhook</p>
        <ul className="mt-2 text-sm text-slate-400 space-y-1">
          <li>• <b className="text-slate-200">Publish</b>: Mon & Thu at 14:00 UTC (≈9 AM Central) — pulls oldest queued topic, writes with Claude, publishes.</li>
          <li>• <b className="text-slate-200">Auto top-up</b>: if queue drops below 20 topics, the system generates ~100 more automatically.</li>
          <li>• <b className="text-slate-200">Cross-post webhook</b>: every publish fires a webhook with a pre-drafted LinkedIn + X post. Plug into Make.com or Zapier (set <code className="text-[#D9B36C]">NEW_POST_WEBHOOK_URL</code> env var) to auto-post to social. <a href="https://make.com" target="_blank" rel="noreferrer" className="text-[#D9B36C] underline">Setup guide →</a></li>
          <li>• <b className="text-slate-200">Self-healing</b>: catches up on missed windows and resets stuck jobs on every backend restart.</li>
        </ul>
        <div className="mt-4 flex gap-3 flex-wrap text-sm items-center">
          <span className="px-3 py-1 rounded-full bg-[#1A1D25] border border-[#272B36]">Queued: <b className="text-white">{queued.length}</b></span>
          <span className="px-3 py-1 rounded-full bg-[#1A1D25] border border-[#272B36]">Published: <b className="text-white">{done.length}</b></span>
          <span className="px-3 py-1 rounded-full bg-[#1A1D25] border border-[#272B36]">Failed: <b className="text-white">{failed.length}</b></span>
          <button onClick={bulkGenerate} disabled={bulking} data-testid="queue-bulk-generate" className="ml-auto inline-flex items-center gap-2 border border-[#D9B36C]/40 bg-[#D9B36C]/10 text-[#D9B36C] font-semibold px-4 py-1.5 rounded-full disabled:opacity-50">
            {bulking ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {bulking ? "Generating…" : "AI-generate 100 topics"}
          </button>
          <button onClick={runNow} disabled={running} data-testid="queue-run-now" className="inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-4 py-1.5 rounded-full disabled:opacity-50">
            {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Run next now
          </button>
        </div>
      </div>

      <form onSubmit={add} className="mt-6 rounded-2xl border border-[#272B36] bg-[#121419] p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Add topic to queue</label>
          <input data-testid="queue-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. How Twitch streamers should handle DMCA strikes from music labels" className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#D9B36C]" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Category</label>
            <select data-testid="queue-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Template</label>
            <select data-testid="queue-template" value={template} onChange={(e) => setTemplate(e.target.value)} className="mt-2 w-full bg-[#1A1D25] border border-[#272B36] rounded-xl px-4 py-3 text-white text-sm">
              {TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-[#D9B36C]" />
          Mark as featured
        </label>
        <button type="submit" data-testid="queue-add-btn" className="inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-5 py-2.5 rounded-full text-sm">
          + Queue topic
        </button>
      </form>

      <div className="mt-6 space-y-3" data-testid="queue-list">
        {loading && <p className="text-slate-500">Loading…</p>}
        {!loading && items.length === 0 && <p className="text-slate-500">Queue empty. Add topics to start auto-publishing.</p>}
        {items.map((t) => (
          <div key={t.id} className="rounded-xl border border-[#272B36] bg-[#121419] p-4 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                  t.status === "queued" ? "bg-[#D9B36C]/15 text-[#D9B36C]" :
                  t.status === "published" ? "bg-emerald-500/15 text-emerald-400" :
                  t.status === "failed" ? "bg-red-500/15 text-red-400" :
                  "bg-amber-500/15 text-amber-400"
                }`}>{t.status}</span>
                <span className="text-xs text-slate-500">{t.category}</span>
              </div>
              <p className="mt-2 text-white text-sm">{t.topic}</p>
              {t.published_slug && <a href={`/blog/${t.published_slug}`} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-[#D9B36C]">→ /blog/{t.published_slug}</a>}
              {t.error && <p className="mt-1 text-xs text-red-400">{t.error}</p>}
            </div>
            <button onClick={() => remove(t.id)} className="text-slate-500 hover:text-red-400" data-testid={`queue-delete-${t.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function WeeklyBrief({ token }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await adminApi(token).get("/admin/weekly-brief/history");
      setHistory(data.items || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, [token]);

  const doPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const { data } = await adminApi(token).post("/admin/weekly-brief/preview");
      setPreview(data);
      toast.success(`Preview generated · ${data.news_items_found} items pulled`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.detail || "Preview failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the weekly brief to ALL active newsletter subscribers right now?")) return;
    setSending(true);
    try {
      const { data } = await adminApi(token).post("/admin/weekly-brief/send-now");
      toast.success(`Sent to ${data.sent} of ${data.total_subscribers} subscribers`);
      loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail || "Send failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-brief">
      <div className="rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-6">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">The Brief · Weekly Newsletter</p>
        <p className="mt-2 text-white font-display font-semibold text-lg">Sends every Friday · 15:00 UTC</p>
        <ul className="mt-2 text-sm text-slate-400 space-y-1">
          <li>• Pulls creator-economy legal news from the past 7 days (Google News + Verge + TechCrunch)</li>
          <li>• Claude synthesizes the top 5–7 items with an Upload Counsel "why it matters" legal angle</li>
          <li>• Emails all active newsletter subscribers via Resend, with unsubscribe link</li>
          <li>• Preview any time. Force-send whenever you want.</li>
        </ul>
        <div className="mt-5 flex gap-3 flex-wrap">
          <button onClick={doPreview} disabled={loadingPreview} data-testid="brief-preview-btn" className="inline-flex items-center gap-2 border border-[#272B36] text-white font-medium px-5 py-2.5 rounded-full text-sm hover:bg-white/5 disabled:opacity-50">
            {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {loadingPreview ? "Generating…" : "Preview this week's brief"}
          </button>
          <button onClick={sendNow} disabled={sending} data-testid="brief-send-btn" className="inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send now"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-6 rounded-2xl border border-[#272B36] bg-[#121419] p-6" data-testid="brief-preview-content">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Preview</p>
          <h3 className="mt-2 font-display font-bold text-white text-2xl">{preview.brief?.subject}</h3>
          <p className="mt-3 text-slate-300 text-sm leading-relaxed">{preview.brief?.intro}</p>
          <div className="mt-6 space-y-5">
            {(preview.brief?.items || []).map((it, i) => (
              <div key={i} className="border-t border-[#272B36] pt-4">
                <a href={it.url} target="_blank" rel="noreferrer" className="text-white font-semibold hover:text-[#D9B36C]">{it.headline}</a>
                <p className="mt-1 text-xs text-slate-500 uppercase tracking-widest">{it.source}</p>
                <p className="mt-2 text-sm text-slate-300"><span className="text-[#D9B36C] font-semibold">Why it matters:</span> {it.why_it_matters}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-slate-300 text-sm border-t border-[#272B36] pt-4">{preview.brief?.closer}</p>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8" data-testid="brief-history">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-400">Send history</p>
          <div className="mt-3 space-y-2">
            {history.map((h) => (
              <div key={h.id} className="rounded-xl border border-[#272B36] bg-[#121419] p-4">
                <p className="text-white font-medium">{h.subject}</p>
                <p className="mt-1 text-xs text-slate-500">{new Date(h.sent_at).toLocaleString()} · {(h.items || []).length} items</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function Inboxx({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi(token).get("/admin/consultations");
        setItems(data.items || []);
      } catch (e) {
        toast.error("Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <p className="text-slate-500">Loading…</p>;
  if (!items.length) return <p className="text-slate-500" data-testid="inbox-empty">No consultation requests yet.</p>;

  return (
    <div className="space-y-3" data-testid="admin-inbox">
      {items.map((c) => (
        <div key={c.id} className="rounded-2xl border border-[#272B36] bg-[#121419] p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display font-semibold text-white">{c.name}</p>
              <p className="text-sm text-[#D9B36C]">{c.email}</p>
            </div>
            <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</span>
          </div>
          <div className="mt-3 grid sm:grid-cols-4 gap-2 text-xs text-slate-400">
            <span>{c.service_interest || "-"}</span>
            <span>{c.urgency || "-"}</span>
            <span>{c.location || "-"}</span>
            <span>{c.platforms || "-"}</span>
            <span>{c.creator_type || "-"}</span>
            <span>{c.audience_size || "-"}</span>
            <span>{c.budget_range || "-"}</span>
            <span>{c.deadline ? `Deadline: ${c.deadline}` : "-"}</span>
          </div>
          {c.phone && <p className="mt-2 text-xs text-slate-400">Phone: {c.phone}</p>}
          <p className="mt-2 text-xs text-slate-400">
            Consent: {c.consent ? "yes" : "no"} · Referral acknowledged: {c.referral_ack ? "yes" : "no"}
          </p>
          <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap">{c.message}</p>
        </div>
      ))}
    </div>
  );
}

function NetworkInbox({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi(token).get("/admin/network-applications");
        setItems(data.items || []);
      } catch (e) {
        toast.error("Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <p className="text-slate-500">Loading…</p>;
  if (!items.length) return <p className="text-slate-500" data-testid="network-empty">No attorney applications yet.</p>;

  const setStatus = async (id, status) => {
    try {
      const { data } = await adminApi(token).patch(`/admin/network-applications/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status: data.status, status_at: new Date().toISOString() } : x)));
    } catch (e) {
      toast.error("Could not update");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-network">
      {items.map((a) => (
        <div key={a.id} className="rounded-2xl border border-[#272B36] bg-[#121419] p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-xl text-white">{a.name}{a.firm ? `, ${a.firm}` : ""}</p>
              <p className="text-sm text-[#D9B36C]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
            </div>
            <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-slate-400">
            <span>Licensed in: {a.bar_states}</span>
            <span>Bar no.: {a.bar_numbers || "-"}</span>
            <span>Areas: {a.practice_areas}</span>
            <span>Years: {a.years_licensed || "-"} · Billing: {a.fee_style || "-"} · Insured: {a.insured || "-"}</span>
          </div>
          {a.profile_url && <p className="mt-2 text-xs text-slate-400 break-all">{a.profile_url}</p>}
          {a.note && <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap">{a.note}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-400" data-testid={`network-status-${a.id}`}>
              Status: <span className="text-white">{a.status || "new"}</span>
              {a.status_at ? ` (${new Date(a.status_at).toLocaleDateString()})` : ""}
            </span>
            {["verified", "declined", "new"].filter((st) => st !== (a.status || "new")).map((st) => (
              <button
                key={st}
                onClick={() => setStatus(a.id, st)}
                className="rounded-full border border-[#272B36] px-3 py-1 text-slate-300 hover:border-[#D9B36C] hover:text-white"
              >
                {st === "verified" ? "Mark verified (bar checked)" : st === "declined" ? "Decline" : "Reset to new"}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
